import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { MessageSignature } from './admin/MessageSignature';
import MessageAttachment from './MessageAttachment';
import StaffComposer from './admin/StaffComposer';
import RichMessageBody, { hasRichBody } from './RichMessageBody';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ── Helpers ───────────────────────────────────────────────────────────────────

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const AVATAR_COLORS = ['#3a6ad9','#3a6ad9','#3a6ad9','#acf44e','#ff082f','#d97706','#db2777'];

function nameToColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(first, last) {
  return `${(first || '').charAt(0)}${(last || '').charAt(0)}`.toUpperCase() || '?';
}

function InitialsAvatar({ first, last, size = 36 }) {
  const name = `${first} ${last}`.trim();
  const bg = nameToColor(name);
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      backgroundColor: bg, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.36, fontWeight: '700', flexShrink: 0,
    }}>
      {getInitials(first, last)}
    </div>
  );
}

const PRIORITY_CONFIG = {
  low:    { bg: '#acf44e', color: '#1a1a1a', label: 'Low' },
  normal: { bg: '#f3f4f6', color: '#6b7280', label: 'Normal' },
  high:   { bg: '#fff7ed', color: '#e6a820', label: 'High' },
  urgent: { bg: '#ff082f', color: '#ffffff', label: 'Urgent' },
};

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'unread', label: 'Unread' },
  { value: 'awaiting_reply', label: 'Awaiting Reply' },
  { value: 'resolved', label: 'Resolved' },
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
];

const SENDER_TYPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'borrower', label: 'Borrowers' },
  { value: 'partner', label: 'Partners' },
];

function FilterSelect({ value, onChange, options, label }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        height: '32px', padding: '0 8px', borderRadius: '8px',
        border: '1px solid #e5e7eb', fontSize: '12px', color: '#374151',
        background: '#fff', outline: 'none', cursor: 'pointer',
      }}
      aria-label={label}
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function AdminMessagesInbox({ type = 'all', title = 'Messages' }) {
  const token = localStorage.getItem('doorly_admin_token');

  const [threads, setThreads]         = useState([]);
  const [total, setTotal]             = useState(0);
  const [search, setSearch]           = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [senderFilter, setSenderFilter] = useState('');
  const [page, setPage]               = useState(1);
  const [loading, setLoading]         = useState(false);

  const [selected, setSelected]       = useState(null);
  const [messages, setMessages]       = useState([]);
  const [updatingPriority, setUpdatingPriority] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const messagesEndRef = useRef(null);

  const fetchThreads = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/messages/threads`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          type: type === 'all' ? (senderFilter || undefined) : type,
          status: statusFilter || undefined,
          priority: priorityFilter || undefined,
          search: search || undefined,
          page,
          limit: 30,
        },
      });
      setThreads(res.data.threads || []);
      setTotal(res.data.total || 0);
    } catch {}
    setLoading(false);
  }, [token, type, senderFilter, statusFilter, priorityFilter, search, page]);

  useEffect(() => { fetchThreads(); }, [fetchThreads]);

  const openThread = async (thread) => {
    setSelected(thread);
    try {
      const res = await axios.get(`${API}/api/admin/messages/threads/${thread.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(res.data.messages || []);
      fetchThreads();
    } catch {}
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // 🔑 THE COMPOSER (2026-10-31): payload = { body, format: 'rich', attachment_data?, attachment_name? } — the server
  // decides plain vs formatted and checks the file (utils/staffMessageCompose). Resolves true when sent; a refusal
  // (a file that is not what it says, say) is thrown so the composer shows it and keeps the draft.
  const sendReply = async (payload) => {
    if (!selected) return false;
    try {
      const res = await axios.post(
        `${API}/api/admin/messages/threads/${selected.id}/reply`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages((prev) => [...prev, res.data.message]);
      fetchThreads();
      return true;
    } catch (e) {
      throw new Error((e && e.response && e.response.data && e.response.data.error) || 'The message was not sent.');
    }
  };

  const updateStatus = async (threadId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.put(
        `${API}/api/admin/messages/threads/${threadId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelected((prev) => prev ? { ...prev, status: newStatus } : prev);
      fetchThreads();
    } catch {}
    setUpdatingStatus(false);
  };

  const updatePriority = async (threadId, newPriority) => {
    setUpdatingPriority(true);
    try {
      await axios.put(
        `${API}/api/admin/messages/threads/${threadId}/priority`,
        { priority: newPriority },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelected((prev) => prev ? { ...prev, priority: newPriority } : prev);
      setThreads((prev) => prev.map((t) => t.id === threadId ? { ...t, priority: newPriority } : t));
    } catch {}
    setUpdatingPriority(false);
  };

  const totalPages = Math.ceil(total / 30);

  // Empty state message per type
  const emptyMessage = type === 'borrower'
    ? 'No borrower messages yet.'
    : type === 'partner'
    ? 'No partner messages yet. Messages from DoorlyPro partners will appear here.'
    : 'No messages yet.';

  return (
    <AdminLayout hideAssistant>
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Page header */}
        <div style={{ padding: '20px 24px 12px', flexShrink: 0, borderBottom: '1px solid #f3f4f6' }}>
          <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: 0 }}>{title}</h1>
          {total > 0 && (
            <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#6b7280' }}>{total} conversation{total !== 1 ? 's' : ''}</p>
          )}
        </div>

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* ── Thread list (left panel) ─────────────────────────────────────── */}
          <div style={{ width: '360px', flexShrink: 0, borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Filter bar */}
            <div style={{ padding: '12px 14px', borderBottom: '1px solid #f3f4f6', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {/* Search */}
              <input
                type="text"
                placeholder="Search conversations…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                style={{
                  width: '100%', height: '34px', padding: '0 12px',
                  borderRadius: '8px', border: '1px solid #e5e7eb',
                  fontSize: '13px', outline: 'none', boxSizing: 'border-box',
                }}
              />
              {/* Dropdowns */}
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <FilterSelect
                  label="Status"
                  value={statusFilter}
                  onChange={(v) => { setStatusFilter(v); setPage(1); }}
                  options={STATUS_OPTIONS}
                />
                <FilterSelect
                  label="Priority"
                  value={priorityFilter}
                  onChange={(v) => { setPriorityFilter(v); setPage(1); }}
                  options={PRIORITY_OPTIONS}
                />
                {type === 'all' && (
                  <FilterSelect
                    label="Sender Type"
                    value={senderFilter}
                    onChange={(v) => { setSenderFilter(v); setPage(1); }}
                    options={SENDER_TYPE_OPTIONS}
                  />
                )}
              </div>
            </div>

            {/* Thread list */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {loading ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>Loading…</div>
              ) : threads.length === 0 ? (
                <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9ca3af', fontSize: '13px', lineHeight: 1.5 }}>
                  {emptyMessage}
                </div>
              ) : (
                threads.map((thread) => {
                  const isSelected = selected?.id === thread.id;
                  const hasUnread = thread.unread_count > 0;
                  // An Eva answer does NOT clear the thread from the queue. She
                  // is not a human reply: if she got it wrong, dropping the
                  // thread out of "awaiting" hides it until the one-business-day
                  // resolution timeout fires. Distinct badge so staff can see at
                  // a glance which threads Eva has already spoken in.
                  const isEvaAnswered = thread.last_sender_role === 'eva';
                  const isAwaiting = thread.last_sender_role === 'borrower' || isEvaAnswered;
                  const prioConf = PRIORITY_CONFIG[thread.priority] || PRIORITY_CONFIG.normal;

                  return (
                    <div
                      key={thread.id}
                      data-testid={`inbox-thread-${thread.id}`}
                      onClick={() => openThread(thread)}
                      style={{
                        padding: '12px 14px',
                        borderBottom: '1px solid #f3f4f6',
                        cursor: 'pointer',
                        backgroundColor: isSelected ? '#eff4ff' : 'transparent',
                        borderLeft: isSelected ? '3px solid #3a6ad9' : '3px solid transparent',
                        transition: 'background-color 0.1s',
                      }}
                      onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                      onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                        {/* Avatar */}
                        <InitialsAvatar first={thread.first_name} last={thread.last_name} size={36} />

                        <div style={{ flex: 1, minWidth: 0 }}>
                          {/* Row 1: name + timestamp */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                            <span style={{ fontWeight: hasUnread ? '700' : '600', fontSize: '13px', color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '160px' }}>
                              {thread.first_name} {thread.last_name}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                              {hasUnread && (
                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#3a6ad9', flexShrink: 0 }} />
                              )}
                              <span style={{ fontSize: '11px', color: '#9ca3af' }}>{timeAgo(thread.last_message_at)}</span>
                            </div>
                          </div>

                          {/* Row 2: loan# or email, sender type badge */}
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '3px' }}>
                            <span style={{ fontSize: '11px', color: '#6b7280' }}>
                              {thread.loan_number ? `Loan #${thread.loan_number}` : thread.user_email}
                            </span>
                            {type === 'all' && (
                              <span style={{
                                fontSize: '10px', fontWeight: '600', padding: '1px 5px', borderRadius: '9999px',
                                backgroundColor: thread.sender_type === 'partner' ? '#f0fde4' : '#eff6ff',
                                color: thread.sender_type === 'partner' ? '#acf44e' : '#3a6ad9',
                              }}>
                                {thread.sender_type === 'partner' ? 'Partner' : 'Borrower'}
                              </span>
                            )}
                            {/* 🔑 WHICH FIRM, AND WHETHER THEY SPEAK FOR IT. A partner
                                thread named only the person, so a rep had no way to
                                tell which account the message concerned without a
                                second lookup — and when the sender is the account's
                                broker, the message is usually ABOUT the firm. */}
                            {thread.partner_company_name && (
                              <span style={{ fontSize: '10px', fontWeight: 600, padding: '1px 5px', borderRadius: 9999,
                                backgroundColor: '#f3f4f6', color: '#4b5563', maxWidth: 160,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {thread.partner_company_name}
                              </span>
                            )}
                            {thread.partner_account_role === 'broker' && (
                              <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 5px', borderRadius: 9999,
                                backgroundColor: '#eff4ff', color: '#3a6ad9' }}>
                                Account
                              </span>
                            )}
                          </div>

                          {/* Row 3: subject */}
                          <div style={{ fontSize: '12px', color: '#374151', fontWeight: hasUnread ? '600' : '400', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: '3px' }}>
                            {thread.subject}
                          </div>

                          {/* Row 4: last message preview */}
                          <div style={{ fontSize: '12px', color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {thread.last_message_body || '—'}
                          </div>

                          {/* Row 5: badges */}
                          <div style={{ display: 'flex', gap: '5px', marginTop: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                            {hasUnread && (
                              <span style={{ backgroundColor: '#3a6ad9', color: '#fff', borderRadius: '9999px', fontSize: '10px', fontWeight: '700', padding: '1px 6px' }}>
                                {thread.unread_count} new
                              </span>
                            )}
                            {isAwaiting && !hasUnread && (
                              <span style={{
                                backgroundColor: isEvaAnswered ? '#eef3fd' : '#fff7ed',
                                color: isEvaAnswered ? '#3a6ad9' : '#e6a820',
                                borderRadius: '9999px', fontSize: '10px', fontWeight: '600', padding: '1px 6px',
                              }}>
                                {isEvaAnswered ? 'Eva answered' : 'Awaiting Reply'}
                              </span>
                            )}
                            {(thread.priority === 'urgent' || thread.priority === 'high') && (
                              <span style={{ fontSize: '10px', fontWeight: '600', padding: '1px 6px', borderRadius: '9999px', backgroundColor: prioConf.bg, color: prioConf.color }}>
                                {prioConf.label}
                              </span>
                            )}
                            {thread.status === 'closed' && (
                              <span style={{ fontSize: '10px', fontWeight: '600', padding: '1px 6px', borderRadius: '9999px', backgroundColor: '#f3f4f6', color: '#6b7280' }}>
                                Resolved
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ padding: '10px 14px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: '11px', color: '#9ca3af' }}>{total} total</span>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '3px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', opacity: page === 1 ? 0.4 : 1 }}>‹</button>
                  <span style={{ fontSize: '11px', color: '#6b7280', padding: '3px 6px' }}>{page}/{totalPages}</span>
                  <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ padding: '3px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', opacity: page === totalPages ? 0.4 : 1 }}>›</button>
                </div>
              </div>
            )}
          </div>

          {/* ── Conversation panel (right) ───────────────────────────────────── */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {!selected ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '8px', color: '#9ca3af' }}>
                <svg width="40" height="40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" /></svg>
                <span style={{ fontSize: '14px' }}>Select a conversation</span>
              </div>
            ) : (
              <>
                {/* Thread header */}
                <div style={{ padding: '14px 20px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                    {/* Left: identity */}
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', minWidth: 0 }}>
                      <InitialsAvatar first={selected.first_name} last={selected.last_name} size={40} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: '700', fontSize: '14px', color: '#111827' }}>
                          {selected.first_name} {selected.last_name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '1px' }}>
                          {selected.loan_number ? `Loan #${selected.loan_number} · ` : ''}{selected.user_email}
                        </div>
                        <div style={{ fontSize: '12px', color: '#374151', marginTop: '2px', fontWeight: '500' }}>
                          {selected.subject}
                        </div>
                      </div>
                    </div>

                    {/* Right: controls */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                      {/* Priority dropdown */}
                      <select
                        value={selected.priority || 'normal'}
                        disabled={updatingPriority}
                        onChange={(e) => updatePriority(selected.id, e.target.value)}
                        style={{
                          height: '32px', padding: '0 8px', borderRadius: '8px',
                          border: `1px solid ${PRIORITY_CONFIG[selected.priority || 'normal'].color}30`,
                          backgroundColor: PRIORITY_CONFIG[selected.priority || 'normal'].bg,
                          color: PRIORITY_CONFIG[selected.priority || 'normal'].color,
                          fontSize: '12px', fontWeight: '600', cursor: 'pointer', outline: 'none',
                        }}
                      >
                        {PRIORITY_OPTIONS.filter((o) => o.value).map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>

                      {/* Resolve / Reopen */}
                      <button
                        onClick={() => updateStatus(selected.id, selected.status === 'open' ? 'closed' : 'open')}
                        disabled={updatingStatus}
                        style={{
                          padding: '5px 14px', borderRadius: '8px', border: '1px solid',
                          cursor: 'pointer', fontSize: '12px', fontWeight: '600',
                          borderColor: selected.status === 'open' ? '#acf44e' : '#6b7280',
                          backgroundColor: selected.status === 'open' ? '#f0fde4' : '#f3f4f6',
                          color: selected.status === 'open' ? '#acf44e' : '#374151',
                          opacity: updatingStatus ? 0.6 : 1,
                        }}
                      >
                        {selected.status === 'open' ? 'Resolve' : 'Reopen'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {messages.map((msg) => {
                    // THREE-way. The old `isSupport ? … : …` put every non-support
                    // row on the borrower side, so an Eva reply rendered with the
                    // BORROWER's initials avatar — staff would read her words as
                    // their client's. isOutbound keeps her on the Doorly side;
                    // isEva labels her so she is not read as a colleague either.
                    const isEva = msg.sender_role === 'eva';
                    const isSupport = msg.sender_role === 'support';
                    const isOutbound = isSupport || isEva;
                    return (
                      <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isOutbound ? 'flex-end' : 'flex-start' }}>
                        {isEva && (
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#3a6ad9', marginBottom: 3 }}>
                            Eva · AI assistant
                          </span>
                        )}
                        <div style={{ display: 'flex', justifyContent: isOutbound ? 'flex-end' : 'flex-start', width: '100%' }}>
                        {!isOutbound && (
                          <div style={{ marginRight: '8px', alignSelf: 'flex-end' }}>
                            <InitialsAvatar first={selected.first_name} last={selected.last_name} size={28} />
                          </div>
                        )}
                        <div style={{
                          maxWidth: '68%',
                          padding: '10px 14px',
                          borderRadius: isOutbound ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                          // Eva gets her own fill — neither the staff blue nor
                          // the borrower grey — so she is visually distinct from
                          // both sides of the conversation at a glance.
                          backgroundColor: isEva ? '#eef3fd' : isSupport ? '#3a6ad9' : '#f3f4f6',
                          color: isSupport ? '#fff' : '#111827',
                          fontSize: '13px',
                          lineHeight: '1.55',
                        }}>
                          {hasRichBody(msg)
                            ? <RichMessageBody msg={msg} dark={isSupport} />
                            : <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.body}</div>}
                          {/* 🚨 NO /uploads HREF (Jesse, 2026-10-28): the public mount is closed; an attachment opens
                              through the signed, principal-scoped attachment route. A legacy row whose bytes are gone
                              answers 410 and reads "Attachment unavailable". */}
                          {msg.has_attachment && msg.attachment_name && (
                            <div style={{ marginTop: 8 }}>
                              <MessageAttachment msgId={msg.id} name={msg.attachment_name} token={token} dark={isSupport} />
                            </div>
                          )}
                          <div style={{ fontSize: '11px', marginTop: '4px', opacity: 0.65, textAlign: 'right' }}>
                            {timeAgo(msg.created_at)}
                          </div>
                        </div>
                        </div>
                        {isSupport && (msg.sender_first_name || msg.sender_title || msg.sender_nmls) && (
                          <div style={{ maxWidth: '68%' }}>
                            <MessageSignature sender={msg} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply area */}
                <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
                  {/* ⚠️ BOTH BRANCHES PUT A CONTROL IN THAT CORNER — Send Reply
                      on one, Reopen on the other — so both were under the
                      launcher, and suppressing it clears both at once. A gutter
                      would have had to be applied twice and the second one is
                      the half nobody would think to re-report.

                      ⚠️ AND THIS COMMENT SITS ABOVE THE TERNARY, NOT INSIDE IT.
                      A JSX comment as the first thing in a branch parses as an
                      object literal and fails on the next token. */}
                  {selected.status === 'closed' ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: '10px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}>
                      <span style={{ fontSize: '13px', color: '#6b7280' }}>This conversation is resolved.</span>
                      <button
                        onClick={() => updateStatus(selected.id, 'open')}
                        style={{ padding: '5px 14px', borderRadius: '8px', border: '1px solid #6b7280', background: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: '#374151' }}
                      >
                        Reopen
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* 🔑 THE STAFF COMPOSER (2026-10-31): formatting + an attachment, to a borrower or a partner thread.
                          Send stays bottom-right, clear of the (hidden) launcher — see hideAssistant below. */}
                      <StaffComposer key={selected.id} token={token} onSend={sendReply} />
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
